/**
 * ✅ Migrated to Axios - 2026-01-07
 * 
 * Salesforce Authentication Service using GraphQL
 * 
 * Handles Salesforce OAuth 2.0 authentication flow via GraphQL API.
 * Uses Axios for simpler GraphQL requests (removed Apollo Client).
 * Auth endpoints are PUBLIC - they don't require auth tokens.
 * 
 * @module services/salesforce/auth.graphql
 */

import { createAxiosInstance } from '@/dataProvider/axios';
import { env } from '@/config/env';
import type { SalesforceEnvironment } from '@/services/salesforce';

// Create a public axios instance for OAuth (skipAuth = true)
// These endpoints are public and don't require authentication
const authAxios = createAxiosInstance(env.OAUTH_GRAPHQL_ENDPOINT, true);

/**
 * GraphQL Response Types
 */
export interface GetAuthorizationUrlResponse {
  authorizationUrl: {
    url: string;
    state: string;
  };
}

interface GraphQLErrorShape {
  message?: string;
  code?: string;
  path?: unknown;
}

interface GraphQLResponse<T> {
  data?: T | null;
  errors?: GraphQLErrorShape[];
}

function graphQLErrorMessage(errors: GraphQLErrorShape[] | undefined, fallback: string): string {
  if (!errors?.length) return fallback;
  return errors.map((error) => error.message || error.code || fallback).filter(Boolean).join("; ");
}

export interface GetAuthorizationUrlVariables {
  environment: string;
}

export interface GenerateJwtResponse {
  generateJwt: {
    user: {
      id: string;
      email: string;
    };
    organisations: Array<{
      id: string;
      orgName: string;
      metadataTypeCounts:  Array<{count: number; type: string;}> | null;
    }>;
    jwtToken: string;
    refreshToken: string;
  };
}

export interface GenerateJwtVariables {
  input: {
    secretKey: string;
  };
}

export interface RefreshJwtResponse {
  refreshJwt: {
    user: {
      id: string;
      email: string;
    };
    organisations: Array<{
      id: string;
      orgName: string;
      orgType?: string;
      instanceUrl?: string;
      metadataTypeCounts?: Array<{ count: number; metadataType: string }> | null;
    }>;
    jwtToken: string;
    refreshToken: string;
  };
}

export interface RefreshJwtVariables {
  input: {
    refreshToken: string;
  };
}

/**
 * Get Salesforce OAuth authorization URL using GraphQL via Axios
 * 
 * @param environment - Salesforce environment (production/sandbox/test_sandbox)
 * @returns Authorization URL and state
 * 
 * @example
 * const data = await getAuthorizationUrl('sandbox');
 * console.log(data.authorizationUrl.url); // Salesforce auth URL
 * console.log(data.authorizationUrl.state); // CSRF state token
 */
export async function getAuthorizationUrl(
  environment: SalesforceEnvironment
): Promise<GetAuthorizationUrlResponse> {
  console.log('🔐 Fetching authorization URL via GraphQL:', { 
    environment,
    endpoint: env.OAUTH_GRAPHQL_ENDPOINT 
  });

  // GraphQL query as plain string
  const query = `
    query GetAuthorizationUrl($environment: String!) {
      authorizationUrl(environment: $environment) {
        url
        state
      }
    }
  `;

  const response = await authAxios.post<GraphQLResponse<GetAuthorizationUrlResponse>>('', {
    query,
    variables: { environment },
  });

  if (response.data.errors?.length) {
    throw new Error(graphQLErrorMessage(response.data.errors, "Authorization URL request failed"));
  }

  const data = response.data.data;

  if (!data?.authorizationUrl?.url) {
    throw new Error('No data received from GraphQL query');
  }

  return data;
}

/**
 * Generate JWT token from secret key using GraphQL via Axios
 * 
 * @param secretKey - Secret key received from backend callback
 * @returns JWT token, user data, and organizations
 * 
 * @example
 * const data = await generateJwtFromSecretKey('secret_key_123');
 * console.log(data.generateJwt.jwtToken);
 * console.log(data.generateJwt.user);
 * console.log(data.generateJwt.organisations);
 */
export async function generateJwtFromSecretKey(
  secretKey: string
): Promise<GenerateJwtResponse> {
  // GraphQL mutation as plain string
  const query = `
    mutation GenerateJwt($input: GenerateJwtInput!) {
      generateJwt(input: $input) {
        user {
          id
          email
        }
        organisations {
          id
          orgName
          orgType
          instanceUrl
          metadataTypeCounts {
            count
            metadataType
          }
        }
        jwtToken
        refreshToken
      }
    }
  `;

  const response = await authAxios.post<GraphQLResponse<GenerateJwtResponse>>('', {
    query,
    variables: { 
      input: { 
        secretKey 
      } 
    },
  });

  if (response.data.errors?.length) {
    throw new Error(graphQLErrorMessage(response.data.errors, "JWT generation failed"));
  }

  const data = response.data.data;

  if (!data?.generateJwt?.user || !data.generateJwt.jwtToken || !data.generateJwt.refreshToken) {
    throw new Error('No data received from GraphQL mutation');
  }

  return data;
}

/**
 * Refresh JWT using refresh token (token rotation: old refresh token is invalidated)
 *
 * @param refreshToken - Refresh token from generateJwt or previous refreshJwt
 * @returns New jwtToken, refreshToken, user, and organisations
 */
export async function refreshJwt(refreshToken: string): Promise<RefreshJwtResponse> {
  const query = `
    mutation RefreshJwt($input: RefreshJwtInput!) {
      refreshJwt(input: $input) {
        user {
          id
          email
        }
        organisations {
          id
          orgName
          orgType
          instanceUrl
          metadataTypeCounts {
            count
            metadataType
          }
        }
        jwtToken
        refreshToken
      }
    }
  `;

  try {
    const response = await authAxios.post<GraphQLResponse<RefreshJwtResponse>>('', {
      query,
      variables: { input: { refreshToken } },
    });

    const data = response.data?.data;
    const errors = response.data?.errors;

    if (errors && Array.isArray(errors) && errors.length > 0) {
      throw new Error(graphQLErrorMessage(errors, "refreshJwt failed"));
    }

    if (!data || !data.refreshJwt) {
      throw new Error('No data received from refreshJwt mutation');
    }

    return data;
  } catch (err) {
    console.error('[refreshJwt] error:', err);
    throw err;
  }
}
